/**
 * AjustesAdminV2 - Admin settings with horizontal pill navigation.
 * Design from Lovable Configuracion.tsx, logic from chirigota.
 * Sections: Perfil, Seguridad, Empresa, Retribución Flexible, Integraciones
 */
import React, { useContext, useEffect, useState, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
	Settings,
	User,
	Shield,
	Building2,
	CreditCard,
	Link2,
	Eye,
	EyeOff,
	ChevronDown,
	Plus,
	Loader2,
	Mail,
	HeadphonesIcon
} from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { PageHeader } from '../../../components/ui/page-header';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogFooter
} from '../../../components/ui/dialog';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger
} from '../../../components/ui/dropdown-menu';
import { ArcaContext } from '../../../context/ArcaContext.context';
import { I18nContext } from '../../../context/translations/TranslationsContext.context';
import updateUserPassword from '../../../api/users/updateUserPassword';
import updateUserLanguage from '../../../api/users/updateUserLanguage';
import getOptionByName from '../../../api/generic-api/getOptionByName';
import getManagementUsersByCompanyId from '../../../api/users/getManagementUsersByCompanyId';
import createManagementUser from '../../../api/users/createManagementUser';
import resendGestorInvite from '../../../api/users/resendGestorInvite';
import { UserI } from '../../../api/users/interfaces/Users';
import FilaGestoria from './FilaGestoria';
import FeedbackModal from '../../../components/Modals/FeedbackModal';
import AdminIntegrationsV2 from '../../../components/Integrations/AdminIntegrationsV2';
import AdminFlexibleCompensationConfigV2 from '../../../components/FlexibleCompensation/AdminFlexibleCompensationConfigV2';
import { cn } from '../../../lib/utils';
import getCurrentUser from '../../../api/users/getCurrentUser';
import { resolveCompanyName } from '../../../utils/resolveCompanyName';
import BorrarCuenta from '../../../movil/BorrarCuenta';
import EnlacePrivacidad from '../../../movil/EnlacePrivacidad';
import InterruptorBiometria from '../../../movil/InterruptorBiometria';

// Section definitions with icons
const SECTIONS = [
	{ id: 'perfil', labelKey: 'user.settings.profile', icon: User },
	{ id: 'seguridad', labelKey: 'user.settings.security', icon: Shield },
	{ id: 'empresa', labelKey: 'user.settings.company', icon: Building2 },
	{ id: 'flexcomp', labelKey: 'admin.flexibleCompensation.tabTitle', icon: CreditCard },
	{ id: 'integraciones', labelKey: 'user.settings.configurationAndIntegrations', icon: Link2 },
	{ id: 'soporte', labelKey: 'user.settings.support', icon: HeadphonesIcon }
] as const;

const fadeUp = {
	hidden: { opacity: 0, y: 16 },
	visible: (i: number) => ({
		opacity: 1,
		y: 0,
		transition: { delay: i * 0.08, duration: 0.4, ease: [0, 0, 0.58, 1] as const }
	})
};

// Horizontal pill navigation
interface SectionNavProps {
	activeSection: string;
	onClickSection: (id: string) => void;
	translate: (key: string) => string;
}

const SectionNav: React.FC<SectionNavProps> = ({ activeSection, onClickSection, translate }) => {
	const activeRef = useRef<HTMLButtonElement>(null);
	const scrollContainerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (activeRef.current && scrollContainerRef.current) {
			const container = scrollContainerRef.current;
			const pill = activeRef.current;
			const pillLeft = pill.offsetLeft;
			const pillWidth = pill.offsetWidth;
			const containerWidth = container.offsetWidth;
			const scrollLeft = container.scrollLeft;

			if (pillLeft < scrollLeft + 16) {
				container.scrollTo({ left: Math.max(0, pillLeft - 16), behavior: 'smooth' });
			} else if (pillLeft + pillWidth > scrollLeft + containerWidth - 16) {
				container.scrollTo({
					left: pillLeft + pillWidth - containerWidth + 16,
					behavior: 'smooth'
				});
			}
		}
	}, [activeSection]);

	return (
		<div className="sticky top-0 z-30 bg-background border-b border-border py-3 mb-6">
			<div
				ref={scrollContainerRef}
				className="flex gap-1.5 overflow-x-auto"
				style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
			>
				{SECTIONS.map((s) => {
					const Icon = s.icon;
					const isActive = activeSection === s.id;
					return (
						<button
							key={s.id}
							ref={isActive ? activeRef : null}
							onClick={() => onClickSection(s.id)}
							className={cn(
								'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all duration-200 shrink-0',
								isActive && 'bg-primary/10 text-primary',
								!isActive && 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
							)}
						>
							<Icon className="w-3.5 h-3.5" />
							{translate(s.labelKey)}
						</button>
					);
				})}
			</div>
		</div>
	);
};

// Section header component
interface SectionHeaderProps {
	icon: React.ElementType;
	title: string;
	description?: string;
}

const SectionHeader: React.FC<SectionHeaderProps> = ({ icon: Icon, title, description }) => (
	<div className="flex items-start gap-3 mb-5">
		<div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
			<Icon className="w-5 h-5 text-primary" />
		</div>
		<div>
			<h2 className="text-lg font-semibold text-foreground">{title}</h2>
			{description && <p className="text-sm text-muted-foreground mt-0.5">{description}</p>}
		</div>
	</div>
);

const AjustesAdminV2: React.FC = () => {
	const {
		state: { translate }
	} = useContext(I18nContext);
	const { dispatch } = useContext(I18nContext);
	const { token, userId, language, setLanguage, companyId } = useContext(ArcaContext);

	// Password change states
	const [currentPassword, setCurrentPassword] = useState('');
	const [newPassword, setNewPassword] = useState('');
	const [repeatPassword, setRepeatPassword] = useState('');
	const [showCurrentPassword, setShowCurrentPassword] = useState(false);
	const [showNewPassword, setShowNewPassword] = useState(false);
	const [showRepeatPassword, setShowRepeatPassword] = useState(false);
	const [currentPasswordError, setCurrentPasswordError] = useState(false);
	const [newPasswordError, setNewPasswordError] = useState(false);
	const [repeatPasswordError, setRepeatPasswordError] = useState(false);
	const [passwordsMatch, setPasswordsMatch] = useState(false);
	const [submittingPassword, setSubmittingPassword] = useState(false);

	// Language states
	const [availableLanguages, setAvailableLanguages] = useState<string[]>([]);

	// Management users states
	const [managementUsers, setManagementUsers] = useState<UserI[]>([]);
	const [loadingManagementUsers, setLoadingManagementUsers] = useState(false);
	const [showManagementModal, setShowManagementModal] = useState(false);
	const [managementEmail, setManagementEmail] = useState('');
	const [managementName, setManagementName] = useState('');
	const [managementPhone, setManagementPhone] = useState('');
	const [isCreatingManagement, setIsCreatingManagement] = useState(false);
	const [managementError, setManagementError] = useState<string | null>(null);
	const [resendingInviteFor, setResendingInviteFor] = useState<string | null>(null);
	const [resendCooldowns, setResendCooldowns] = useState<Record<string, boolean>>({});

	// Feedback states
	const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);
	const [feedbackError, setFeedbackError] = useState(false);
	const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);

	// User info states
	const [userName, setUserName] = useState('');
	const [userEmail, setUserEmail] = useState('');
	const [companyName, setCompanyName] = useState('');

	// Section navigation
	const [activeSection, setActiveSection] = useState<string>(SECTIONS[0].id);
	const isScrollingFromClick = useRef(false);
	const clickTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

	useEffect(() => {
		const fetchData = async () => {
			if (!token) return;

			try {
				// Fetch current user info
				const userResponse = await getCurrentUser(token);
				if (userResponse.success && userResponse.data) {
					setUserName(userResponse.data.user?.name || '');
					setUserEmail(userResponse.data.user?.email || '');
					setCompanyName(resolveCompanyName(userResponse.data));
				}

				// Fetch available languages
				const langResponse = await getOptionByName(token, 'user-available-languages');
				if (langResponse.data?.value) {
					setAvailableLanguages(JSON.parse(langResponse.data.value));
				}

				// Fetch management users
				if (companyId) {
					setLoadingManagementUsers(true);
					const mgmtResponse = await getManagementUsersByCompanyId(token, companyId);
					if (mgmtResponse.success) {
						setManagementUsers(mgmtResponse.data || []);
					}
					setLoadingManagementUsers(false);
				}
			} catch (error) {
				console.error('Error fetching settings data:', error);
			}
		};

		fetchData();
	}, [token, companyId]);

	// Intersection observer for scroll-based section highlighting
	useEffect(() => {
		const observers: IntersectionObserver[] = [];
		SECTIONS.forEach((s) => {
			const el = document.getElementById(s.id);
			if (!el) return;
			const observer = new IntersectionObserver(
				([entry]) => {
					if (entry.isIntersecting && !isScrollingFromClick.current) {
						setActiveSection(s.id);
					}
				},
				{ rootMargin: '-10% 0px -80% 0px', threshold: 0 }
			);
			observer.observe(el);
			observers.push(observer);
		});
		return () => observers.forEach((o) => o.disconnect());
	}, []);

	const handleClickSection = useCallback((id: string) => {
		setActiveSection(id);
		isScrollingFromClick.current = true;
		clearTimeout(clickTimeoutRef.current);
		clickTimeoutRef.current = setTimeout(() => {
			isScrollingFromClick.current = false;
		}, 1000);
		const el = document.getElementById(id);
		if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
	}, []);

	const handleNewPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setNewPassword(e.target.value);
		setNewPasswordError(false);
		setPasswordsMatch(e.target.value === repeatPassword);
	};

	const handleRepeatPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setRepeatPassword(e.target.value);
		setRepeatPasswordError(false);
		setPasswordsMatch(e.target.value === newPassword);
	};

	const handlePasswordSubmit = async () => {
		if (!currentPassword || !newPassword || !repeatPassword) {
			if (!currentPassword) setCurrentPasswordError(true);
			if (!newPassword) setNewPasswordError(true);
			if (!repeatPassword) setRepeatPasswordError(true);
			return;
		}

		if (!passwordsMatch) return;

		setSubmittingPassword(true);
		try {
			const res = await updateUserPassword(token, userId, currentPassword, newPassword);
			if (res.success) {
				setFeedbackMessage(translate('user.userSettings.updatePasswordSuccess'));
				setFeedbackError(false);
				setCurrentPassword('');
				setNewPassword('');
				setRepeatPassword('');
			} else {
				setFeedbackMessage(
					res.message === 'USER_NOT_FOUND_OR_WRONG_PASSWORD'
						? translate('user.userSettings.userNotFoundOrPasswordIncorrect')
						: translate('user.userSettings.messageError')
				);
				setFeedbackError(true);
			}
		} catch (error) {
			setFeedbackMessage(translate('user.userSettings.messageError'));
			setFeedbackError(true);
		} finally {
			setSubmittingPassword(false);
			setIsFeedbackOpen(true);
		}
	};

	const changeLanguage = async (newLanguage: string) => {
		if (newLanguage === language) return;
		const prevLanguage = language;
		setLanguage(newLanguage);
		dispatch({ type: 'setLanguage', payload: newLanguage });
		localStorage.setItem('language', newLanguage);
		try {
			await updateUserLanguage(token, newLanguage);
		} catch (error) {
			console.error('Error updating language:', error);
			setLanguage(prevLanguage);
			dispatch({ type: 'setLanguage', payload: prevLanguage });
			localStorage.setItem('language', prevLanguage);
			setFeedbackMessage(translate('user.userSettings.messageError'));
			setFeedbackError(true);
			setIsFeedbackOpen(true);
		}
	};

	const handleCreateManagement = async () => {
		if (!token || !companyId || !managementEmail || !managementName) return;

		setIsCreatingManagement(true);
		setManagementError(null);

		try {
			const response = await createManagementUser(
				token,
				companyId,
				managementEmail,
				managementName,
				managementPhone || undefined
			);
			if (response.success === false || response.statusCode === 409) {
				throw new Error(
					response.message || translate('admin.dashboard.adminSettings.managementAlreadyExists')
				);
			}
			setShowManagementModal(false);
			setManagementEmail('');
			setManagementName('');
			setManagementPhone('');
			setFeedbackMessage(translate('admin.dashboard.adminSettings.managementCreatedSuccess'));
			setFeedbackError(false);
			// Refresh management users
			const mgmtResponse = await getManagementUsersByCompanyId(token, companyId);
			if (mgmtResponse.success) {
				setManagementUsers(mgmtResponse.data || []);
			}
		} catch (error: unknown) {
			setManagementError(
				error instanceof Error && error.message === 'MANAGEMENT_USER_ALREADY_EXISTS'
					? translate('admin.dashboard.adminSettings.managementAlreadyExists')
					: translate('admin.dashboard.adminSettings.managementCreateError')
			);
		} finally {
			setIsCreatingManagement(false);
			setIsFeedbackOpen(true);
		}
	};

	const handleResendGestorInvite = async (managementUserId: string) => {
		if (!token || !companyId) return;
		setResendingInviteFor(managementUserId);

		try {
			const response = await resendGestorInvite(token, companyId, managementUserId);
			if (response.success === false) {
				throw new Error(
					response.error || translate('admin.dashboard.adminSettings.inviteSentError')
				);
			}
			setFeedbackMessage(translate('admin.dashboard.adminSettings.inviteSentSuccess'));
			setFeedbackError(false);
			setResendCooldowns((prev) => ({ ...prev, [managementUserId]: true }));
			setTimeout(() => {
				setResendCooldowns((prev) => ({ ...prev, [managementUserId]: false }));
			}, 30000);
		} catch {
			setFeedbackMessage(translate('admin.dashboard.adminSettings.inviteSentError'));
			setFeedbackError(true);
		} finally {
			setResendingInviteFor(null);
			setIsFeedbackOpen(true);
		}
	};

	return (
		<motion.div initial="hidden" animate="visible" className="max-w-2xl mx-auto pb-12">
			{/* Cabecera — la pone `ui/PageHeader`, puerto de Proa v0.10.0.
			    ⚠️ El icono se conserva DENTRO del título, y escalado: el `h1` pasa de `text-2xl` a
			    40 px en escritorio, así que un icono de 20 px se quedaba diminuto al lado. Es la
			    única de las seis pantallas de admin con icono en la cabecera — si al verlo no
			    convence, lo que toca es quitarlo, no volver al título pequeño.
			    `width="full"` porque el ancho ya lo pone el contenedor de la pantalla
			    (`max-w-2xl`, justo arriba). */}
			<motion.div custom={0} variants={fadeUp} className="mb-6">
				<PageHeader
					width="full"
					title={
						<span className="inline-flex items-center gap-3">
							<Settings
								className="w-6 h-6 lg:w-8 lg:h-8 shrink-0 text-primary"
								aria-hidden="true"
							/>
							{translate('admin.dashboard.adminSettings')}
						</span>
					}
					subtitle={translate('admin.settings.description')}
				/>
			</motion.div>

			{/* Feedback Modal */}
			<FeedbackModal
				open={isFeedbackOpen}
				onClose={() => {
					setIsFeedbackOpen(false);
					setFeedbackMessage(null);
				}}
				isError={feedbackError}
				message={feedbackMessage || ''}
			/>

			{/* Section Navigation Pills */}
			<SectionNav
				activeSection={activeSection}
				onClickSection={handleClickSection}
				translate={translate}
			/>

			<div className="space-y-6">
				{/* ─── 1. PERFIL ──────────────────────────────────────── */}
				<section id="perfil" className="scroll-mt-28">
					<motion.div custom={1} variants={fadeUp}>
						<Card className="border-none shadow-xs">
							<CardContent className="p-6">
								<SectionHeader
									icon={User}
									title={translate('user.settings.profileTitle')}
									description={translate('user.settings.profileDescription')}
								/>
								<div className="space-y-4">
									<div>
										<Label className="text-sm font-medium mb-1.5 block">
											{translate('user.userSettings.name')}
										</Label>
										<Input value={userName} disabled className="bg-muted/50" />
									</div>
									<div>
										<Label className="text-sm font-medium mb-1.5 block">
											{translate('user.userSettings.email')}
										</Label>
										<Input value={userEmail} disabled className="bg-muted/50" />
										<p className="text-xs text-muted-foreground mt-1">
											{translate('user.settings.emailCannotChange')}
										</p>
									</div>
									<div>
										<Label className="text-sm font-medium mb-1.5 block">
											{translate('user.userSettings.currentLanguage')}
										</Label>
										<DropdownMenu>
											<DropdownMenuTrigger asChild>
												<Button variant="outline" className="w-full sm:w-auto justify-between">
													{translate(`user.userSettings.${language}`)}
													<ChevronDown className="w-4 h-4 ml-2" />
												</Button>
											</DropdownMenuTrigger>
											<DropdownMenuContent>
												{availableLanguages.map((lang) => (
													<DropdownMenuItem key={lang} onClick={() => changeLanguage(lang)}>
														{translate(`user.userSettings.${lang}`)}
													</DropdownMenuItem>
												))}
											</DropdownMenuContent>
										</DropdownMenu>
									</div>
								</div>
							</CardContent>
						</Card>
					</motion.div>
				</section>

				{/* ─── 2. SEGURIDAD ───────────────────────────────────── */}
				<section id="seguridad" className="scroll-mt-28">
					<motion.div custom={2} variants={fadeUp}>
						<Card className="border-none shadow-xs">
							<CardContent className="p-6">
								<SectionHeader
									icon={Shield}
									title={translate('user.userSettings.updatePassword')}
									description={translate('user.settings.securityDescription')}
								/>
								<div className="space-y-4 max-w-md">
									{/* Solo en la app y si el móvil tiene biometría; en la web no pinta nada. */}
									<InterruptorBiometria />
									{/* Current password */}
									<div className="space-y-2">
										<Label>{translate('user.userSettings.currentPassword')}</Label>
										<div className="relative">
											<Input
												type={showCurrentPassword ? 'text' : 'password'}
												value={currentPassword}
												onChange={(e) => {
													setCurrentPassword(e.target.value);
													setCurrentPasswordError(false);
												}}
												className={cn('pr-10', currentPasswordError && 'border-destructive')}
											/>
											<button
												type="button"
												onClick={() => setShowCurrentPassword(!showCurrentPassword)}
												className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
											>
												{showCurrentPassword ? (
													<EyeOff className="w-4 h-4" />
												) : (
													<Eye className="w-4 h-4" />
												)}
											</button>
										</div>
										{currentPasswordError && (
											<p className="text-xs text-destructive">
												{translate('user.userSettings.mandatoryField')}
											</p>
										)}
									</div>

									{/* New password */}
									<div className="space-y-2">
										<Label>{translate('user.userSettings.newPassword')}</Label>
										<div className="relative">
											<Input
												type={showNewPassword ? 'text' : 'password'}
												value={newPassword}
												onChange={handleNewPasswordChange}
												placeholder={translate('user.userSettings.introduceNewPassword')}
												className={cn('pr-10', newPasswordError && 'border-destructive')}
											/>
											<button
												type="button"
												onClick={() => setShowNewPassword(!showNewPassword)}
												className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
											>
												{showNewPassword ? (
													<EyeOff className="w-4 h-4" />
												) : (
													<Eye className="w-4 h-4" />
												)}
											</button>
										</div>
										{newPasswordError && (
											<p className="text-xs text-destructive">
												{translate('user.userSettings.mandatoryField')}
											</p>
										)}
									</div>

									{/* Repeat password */}
									<div className="space-y-2">
										<Label>{translate('user.userSettings.repeatNewPassword')}</Label>
										<div className="relative">
											<Input
												type={showRepeatPassword ? 'text' : 'password'}
												value={repeatPassword}
												onChange={handleRepeatPasswordChange}
												placeholder={translate('user.userSettings.repeatNewPassword')}
												className={cn('pr-10', repeatPasswordError && 'border-destructive')}
											/>
											<button
												type="button"
												onClick={() => setShowRepeatPassword(!showRepeatPassword)}
												className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
											>
												{showRepeatPassword ? (
													<EyeOff className="w-4 h-4" />
												) : (
													<Eye className="w-4 h-4" />
												)}
											</button>
										</div>
										{repeatPasswordError && (
											<p className="text-xs text-destructive">
												{translate('user.userSettings.mandatoryField')}
											</p>
										)}
										{!passwordsMatch && repeatPassword && (
											<p className="text-xs text-destructive">
												{translate('user.userSettings.passwordsDontMatch')}
											</p>
										)}
									</div>

									<Button
										onClick={handlePasswordSubmit}
										disabled={
											!passwordsMatch ||
											!currentPassword ||
											!newPassword ||
											!repeatPassword ||
											submittingPassword
										}
										className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full"
									>
										{submittingPassword && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
										{translate('user.userSettings.updatePassword')}
									</Button>
								</div>
							</CardContent>
						</Card>
					</motion.div>
				</section>

				{/* ─── 3. EMPRESA ─────────────────────────────────────── */}
				<section id="empresa" className="scroll-mt-28">
					<motion.div custom={3} variants={fadeUp}>
						<Card className="border-none shadow-xs">
							<CardContent className="p-6">
								<SectionHeader
									icon={Building2}
									title={translate('user.settings.companyTitle')}
									description={translate('user.settings.companyDescription')}
								/>
								<div className="space-y-4">
									<div>
										<Label className="text-sm font-medium mb-1.5 block">
											{translate('admin.company.companyName')}
										</Label>
										<Input value={companyName} disabled className="bg-muted/50" />
									</div>

									{/* Management users */}
									<div className="pt-4 border-t border-border">
										<div className="flex items-center justify-between mb-4">
											<h3 className="text-sm font-semibold text-foreground">
												{translate('admin.dashboard.adminSettings.managementAccount')}
											</h3>
											{managementUsers.length === 0 && (
												<Button
													variant="outline"
													size="sm"
													onClick={() => {
														setManagementError(null);
														setShowManagementModal(true);
													}}
													className="rounded-full"
												>
													<Plus className="w-4 h-4 mr-2" />
													{translate('admin.dashboard.adminSettings.addManagement')}
												</Button>
											)}
										</div>

										{loadingManagementUsers ? (
											<div className="flex items-center justify-center py-8">
												<Loader2 className="w-6 h-6 animate-spin text-primary" />
											</div>
										) : managementUsers.length > 0 ? (
											<div className="space-y-3">
												{managementUsers.map((user) => (
													<FilaGestoria
														key={user.id}
														usuario={user}
														reenviando={resendingInviteFor === user.id}
														enEspera={Boolean(resendCooldowns[user.id])}
														onReenviar={() => handleResendGestorInvite(user.id)}
														translate={translate}
													/>
												))}
											</div>
										) : (
											<p className="text-muted-foreground text-center py-4 italic text-sm">
												{translate('admin.dashboard.adminSettings.noManagementUsers')}
											</p>
										)}
									</div>
								</div>
							</CardContent>
						</Card>
					</motion.div>
				</section>

				{/* ─── 4. RETRIBUCIÓN FLEXIBLE ────────────────────────── */}
				<section id="flexcomp" className="scroll-mt-28">
					<motion.div custom={4} variants={fadeUp}>
						<AdminFlexibleCompensationConfigV2 />
					</motion.div>
				</section>

				{/* ─── 5. INTEGRACIONES ───────────────────────────────── */}
				<section id="integraciones" className="scroll-mt-28">
					<motion.div custom={5} variants={fadeUp}>
						<AdminIntegrationsV2 />
					</motion.div>
				</section>

				{/* ─── 6. SOPORTE ─────────────────────────────────────── */}
				<section id="soporte" className="scroll-mt-28">
					<motion.div custom={6} variants={fadeUp}>
						<Card className="border-none shadow-xs">
							<CardContent className="p-6">
								<SectionHeader
									icon={HeadphonesIcon}
									title={translate('user.settings.supportTitle')}
									description={translate('user.settings.supportNeedHelp')}
								/>
								<div className="rounded-lg border border-primary/20 bg-primary/5 p-5">
									<div className="flex items-center justify-between gap-4 flex-wrap">
										<div>
											<p className="text-sm font-semibold text-foreground">
												support.arcadigital.app
											</p>
											<p className="text-xs text-muted-foreground mt-1">
												{translate('user.settings.supportTopics')}
											</p>
										</div>
										<Button
											variant="outline"
											size="sm"
											className="rounded-full border-primary text-primary hover:bg-primary/5 gap-1.5 shrink-0"
											onClick={() =>
												window.open(
													'https://support.arcadigital.app/',
													'_blank',
													'noopener,noreferrer'
												)
											}
										>
											<Mail className="w-3.5 h-3.5" />
											{translate('user.settings.contact')}
										</Button>
									</div>
								</div>
								{/* Solo en la app; en la web no pinta nada. */}
								<EnlacePrivacidad />
								<BorrarCuenta />
							</CardContent>
						</Card>
					</motion.div>
				</section>
			</div>

			{/* Management User Modal */}
			<Dialog open={showManagementModal} onOpenChange={setShowManagementModal}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{translate('admin.dashboard.adminSettings.addManagement')}</DialogTitle>
					</DialogHeader>

					<div className="space-y-4 py-4">
						{managementError && <p className="text-sm text-destructive">{managementError}</p>}

						<div className="space-y-2">
							<Label>{translate('admin.dashboard.adminSettings.managementName')}</Label>
							<Input
								value={managementName}
								onChange={(e) => setManagementName(e.target.value)}
								placeholder={translate('admin.dashboard.adminSettings.managementNamePlaceholder')}
							/>
						</div>

						<div className="space-y-2">
							<Label>{translate('admin.dashboard.adminSettings.managementEmail')}</Label>
							<Input
								type="email"
								value={managementEmail}
								onChange={(e) => setManagementEmail(e.target.value)}
								placeholder={translate('admin.dashboard.adminSettings.managementEmailPlaceholder')}
							/>
						</div>

						<div className="space-y-2">
							<Label>{translate('admin.dashboard.adminSettings.managementPhone')}</Label>
							<Input
								type="tel"
								value={managementPhone}
								onChange={(e) => setManagementPhone(e.target.value)}
								placeholder={translate('admin.dashboard.adminSettings.managementPhonePlaceholder')}
							/>
						</div>
					</div>

					<DialogFooter>
						<Button
							variant="ghost"
							onClick={() => {
								setShowManagementModal(false);
								setManagementEmail('');
								setManagementName('');
								setManagementPhone('');
								setManagementError(null);
							}}
						>
							{translate('general.genericModal.close')}
						</Button>
						<Button
							onClick={handleCreateManagement}
							disabled={isCreatingManagement || !managementEmail || !managementName}
							className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full"
						>
							{isCreatingManagement && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
							{translate('admin.dashboard.adminSettings.createManagement')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</motion.div>
	);
};

export default AjustesAdminV2;
